import { Button, Input, Space } from "antd";
import type { InputRef } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import type { LookupItem } from "../../api/types";
import { useLookupPanelStore } from "../../stores/lookupPanelStore";
import { lookupRegistry } from "./lookupRegistry";

let suppressLookupOpenUntil = 0;

export function SmartLookupField({
  lookupKey,
  value,
  label,
  placeholder,
  onChange,
  parentContext,
  showSearchButton = false
}: {
  lookupKey: keyof typeof lookupRegistry;
  value?: string | number | null;
  label?: string;
  placeholder?: string;
  onChange?: (value: { id: string | number | null; label: string; raw?: unknown }) => void;
  parentContext?: Record<string, unknown>;
  showSearchButton?: boolean;
}) {
  const openPanel = useLookupPanelStore((x) => x.openPanel);
  const def = lookupRegistry[lookupKey];
  const [display, setDisplay] = useState(label ?? "");
  const inputRef = useRef<InputRef>(null);

  useEffect(() => {
    setDisplay(label ?? "");
  }, [label]);

  const suppressReopen = () => {
    suppressLookupOpenUntil = Date.now() + 1600;
  };

  const focusInput = () => {
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const open = () => {
    if (Date.now() < suppressLookupOpenUntil) return;

    openPanel({
      ...def,
      searchText: "",
      parentContext,
      suppressAfterSelect: suppressReopen,
      returnFocus: focusInput,
      onSelect: (item: LookupItem) => {
        suppressReopen();
        setDisplay(item.label);
        onChange?.({ id: item.id, label: item.label, raw: item.raw });
        focusInput();
      }
    });
  };

  return (
    <Space.Compact className="smart-lookup" block>
      <Input
        ref={inputRef}
        value={display}
        placeholder={placeholder ?? def.title}
        readOnly
        onClick={open}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            open();
          }
        }}
        onMouseDown={(event) => {
          if (Date.now() < suppressLookupOpenUntil) event.preventDefault();
        }}
        suffix={!showSearchButton ? <DownOutlined className="smart-lookup-caret" /> : undefined}
        style={{ width: showSearchButton ? "calc(100% - 34px)" : "100%" }}
      />
      {showSearchButton ? (
        <Button
          icon={<SearchOutlined />}
          onClick={open}
          tabIndex={-1}
          onMouseDown={(event) => {
            if (Date.now() < suppressLookupOpenUntil) event.preventDefault();
          }}
          aria-label={`Open ${def.title}`}
        />
      ) : null}
      <input type="hidden" value={value ?? ""} readOnly />
    </Space.Compact>
  );
}
